import { queueWarmMessageBodies } from "@/lib/mail/bodyWarmup";
import { getFolderSnapshot } from "@/lib/mail/inboxFolderBus";
import { markBodiesWarming } from "@/lib/mail/mailBodyWarmState";
import { markMailPendingReveal } from "@/lib/mail/mailPendingReveal";
import { requestMailNotificationSound } from "@/lib/mail/mailNotificationSoundGate";
import type { MailMessageSummary } from "@/lib/types/mail";

export const MAIL_NEW_MAIL_EVENT = "mailNewMessages";

export type MailNewMailDetail = {
  accountId: number;
  folder: string;
  newCount: number;
  uids?: number[];
};

/** Anti double-cloche / double-prep (cache puis IMAP annoncent les mêmes UIDs). */
const NOTIFY_DEDUPE_MS = 25_000;
const recentNotifyByFolder = new Map<string, { at: number; uidKey: string }>();

function folderNotifyKey(accountId: number, folder: string): string {
  return `${accountId}\0${folder}`;
}

function uidSetKey(uids: number[]): string {
  return [...uids].sort((a, b) => a - b).join(",");
}

export function countUnreadAmongUids(
  messages: readonly MailMessageSummary[] | undefined,
  uids: number[]
): number {
  if (!messages?.length || uids.length === 0) return 0;
  const want = new Set(uids);
  let n = 0;
  for (const msg of messages) {
    if (want.has(msg.uid) && msg.uid > 0 && !msg.seen) n += 1;
  }
  return n;
}

/**
 * À appeler **avant** `publishFolderSnapshot` pour coller pastille et liste.
 * Pose pending reveal (+ hold via recount) avant que le snapshot n’arrive à l’UI.
 * Skip les UID déjà `bodyCached` sur DD02 (pas de masquage / trickle inutile).
 */
export function markPendingRevealBeforeInboxSnapshot(
  accountId: number,
  folder: string,
  messages: readonly MailMessageSummary[],
  newUids: number[]
): void {
  if (newUids.length === 0) return;
  const unique = [...new Set(newUids.filter((uid) => uid > 0))];
  const needReveal = unique.filter((uid) => {
    const msg = messages.find((m) => m.uid === uid);
    return !msg?.bodyCached;
  });
  if (needReveal.length === 0) return;
  const unreadUids = needReveal.filter((uid) => {
    const msg = messages.find((m) => m.uid === uid);
    return msg != null && !msg.seen;
  });
  markMailPendingReveal(accountId, folder, needReveal, { unreadUids });
}

function alreadyAnnouncedRecently(
  accountId: number,
  folder: string,
  uids: number[]
): boolean {
  if (uids.length === 0) return false;
  const key = folderNotifyKey(accountId, folder);
  const prev = recentNotifyByFolder.get(key);
  if (!prev) return false;
  if (Date.now() - prev.at > NOTIFY_DEDUPE_MS) return false;
  const prevUids = new Set(
    prev.uidKey
      .split(",")
      .map((s) => Number(s))
      .filter((n) => n > 0)
  );
  return uids.every((uid) => prevUids.has(uid));
}

/**
 * Nouveau(x) mail(s) — warm + pending reveal (affichage/son après corps/PJ client).
 */
export function notifyNewMailArrived(
  accountId: number,
  folder: string,
  newCount: number,
  uids: number[] = [],
  options?: {
    /** Pending + hold déjà posés avant publish — ne pas re-marquer / re-add. */
    holdAlreadyApplied?: boolean;
    pendingAlreadyApplied?: boolean;
  }
): void {
  if (newCount < 1) return;

  const unique = [...new Set(uids.filter((uid) => uid > 0))];
  if (alreadyAnnouncedRecently(accountId, folder, unique)) {
    return;
  }

  const snap = getFolderSnapshot(accountId, folder);
  const toWarm = unique.filter((uid) => {
    const msg = snap?.messages.find((m) => m.uid === uid);
    return !msg?.bodyCached;
  });

  if (toWarm.length > 0) {
    markBodiesWarming(accountId, folder, [toWarm[0]]);
    queueWarmMessageBodies(accountId, folder, toWarm, { newMail: true });
  }

  const unreadUids = unique.filter((uid) => {
    const msg = snap?.messages.find((m) => m.uid === uid);
    return msg != null && !msg.seen;
  });

  // Hold = recount pending uniquement (pas d’additif → pas de double compte).
  // Skip UID déjà sur DD02 : visibles tout de suite (pastille serveur).
  if (!options?.pendingAlreadyApplied) {
    const needReveal = unique.filter((uid) => {
      const msg = snap?.messages.find((m) => m.uid === uid);
      return !msg?.bodyCached;
    });
    if (needReveal.length > 0) {
      const unreadNeed = needReveal.filter((uid) => {
        const msg = snap?.messages.find((m) => m.uid === uid);
        return msg != null && !msg.seen;
      });
      markMailPendingReveal(accountId, folder, needReveal, { unreadUids: unreadNeed });
    }
  }

  if (folder.toUpperCase() === "INBOX" && unreadUids.length > 0) {
    requestMailNotificationSound();
  }

  recentNotifyByFolder.set(folderNotifyKey(accountId, folder), {
    at: Date.now(),
    uidKey: uidSetKey(unique),
  });

  window.dispatchEvent(
    new CustomEvent<MailNewMailDetail>(MAIL_NEW_MAIL_EVENT, {
      detail: { accountId, folder, newCount, uids: unique },
    })
  );
}
